import { useRef, useState } from "react";
import { SettingsSection } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import { Button, Select } from "../ui";
import { useSession } from "./session";

/**
 * Settings → Data (spec §9.28): export everything, import an export (merge or replace). Each result is
 * stated in three sentences: what happened, what it means, what to do. Imports from other apps (Super
 * Productivity, Todoist, …) are a section of their own (`ImportSettings`, I4).
 */
export function DataSettings() {
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const isAdmin = useSession((s) => s.session?.profile?.isAdmin);
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  if (!workspaceId || !isAdmin) return null;

  async function post(path: string, file: File, okKey: string) {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(path, {
        method: "POST",
        credentials: "same-origin",
        body: await file.arrayBuffer(),
      });
      const body = (await res.json()) as { error?: string; report?: Record<string, unknown> };
      if (res.ok) setMessage({ ok: true, text: String(t(okKey, { ...(body.report ?? {}) })) });
      else setMessage({ ok: false, text: t(`errors.${body.error}`, { defaultValue: t("error.generic") }) });
    } catch {
      setMessage({ ok: false, text: t("error.generic") });
    }
    setBusy(false);
  }

  return (
    <SettingsSection
      id="settings-data"
      intro={t("settings.intro.data")}
      title={t("data.title")}
      testId="settings-data"
    >
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={`/api/internal/admin/export?workspace=${workspaceId}`}
          download
          data-testid="export-link"
          className="inline-flex h-9 items-center rounded-sm border border-neutral-300 bg-[var(--surface)] px-3.5 text-[13.5px] font-medium hover:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          {t("data.export")}
        </a>
        <span className="text-[12.5px] text-neutral-500">{t("data.exportHint")}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={importRef}
          type="file"
          accept=".zip"
          className="sr-only"
          aria-label={t("data.import")}
          data-testid="import-file"
          onChange={(e) =>
            e.target.files?.[0] &&
            void post(
              `/api/internal/admin/import?workspace=${workspaceId}&mode=${mode}`,
              e.target.files[0],
              "data.imported",
            )
          }
        />
        <Button variant="secondary" disabled={busy} onClick={() => importRef.current?.click()}>
          {t("data.import")}
        </Button>
        <Select
          value={mode}
          onChange={(e) => setMode(e.target.value as "merge" | "replace")}
          aria-label={t("data.mode")}
          data-testid="import-mode"
        >
          <option value="merge">{t("data.merge")}</option>
          <option value="replace">{t("data.replace")}</option>
        </Select>
        <span className="text-[12.5px] text-neutral-500">
          {mode === "merge" ? t("data.mergeHint") : t("data.replaceHint")}
        </span>
      </div>
      {message && (
        <p
          role="status"
          data-testid="data-message"
          className={message.ok ? "text-[13px] text-ok" : "text-[13px] text-err"}
        >
          {message.text}
        </p>
      )}
    </SettingsSection>
  );
}
